import { createContext, useState, useContext, useEffect } from 'react'

import { useRouter } from 'next/router'

interface SavingContextType {
  isSavingBlocked: boolean
  isSavingBlockedWithDelay: boolean // Saving can be very quick, so this delays it a bit to show the user that it's saving
  setIsSavingBlocked: (uploading: boolean) => void
  isDisabed: boolean
}

const SavingContext = createContext<SavingContextType>({
  isSavingBlocked: false,
  isSavingBlockedWithDelay: false,
  setIsSavingBlocked: () => {},
  isDisabed: false,
})

type Props = {
  children?: React.ReactNode
}

export const PreventSavingProvider: React.FC<Props> = ({ children }) => {
  const router = useRouter()

  const [isDisabed, setIsDisabled] = useState(false)
  const [isSavingBlocked, setIsSavingBlocked] = useState(false)
  const [isSavingBlockedWithDelay, setIsSavingBlockWithDelay] = useState(false)

  // toggle the disabled state when navigating, this is prevent double clicking on buttons and such
  useEffect(() => {
    const stopOnNav = () => {
      setIsDisabled(true)
    }

    const resetOnNav = () => {
      setIsDisabled(false)
    }

    router.events.on('routeChangeStart', stopOnNav)
    router.events.on('routeChangeError', resetOnNav)
    router.events.on('routeChangeComplete', resetOnNav)

    return () => {
      router.events.off('routeChangeStart', stopOnNav)
      router.events.off('routeChangeError', resetOnNav)
      router.events.off('routeChangeComplete', resetOnNav)
    }
  }, [router])

  useEffect(() => {
    if (isSavingBlocked && !isSavingBlockedWithDelay) {
      return setIsSavingBlockWithDelay(true)
    }

    // if saving is no longer blocked, but the delayed version is, clear it after a short delay
    if (!isSavingBlocked && isSavingBlockedWithDelay) {
      setTimeout(() => {
        setIsSavingBlockWithDelay(false)
      }, 600)
    }
  }, [isSavingBlocked, isSavingBlockedWithDelay])

  return (
    <SavingContext.Provider
      value={{
        isSavingBlocked,
        setIsSavingBlocked,
        isSavingBlockedWithDelay,
        isDisabed,
      }}
    >
      {children}
    </SavingContext.Provider>
  )
}

export const usePreventSaving = () => useContext(SavingContext)
